/**
 * Billing API Service
 * Frontend client for invoice-based provider-abstracted billing endpoints.
 *
 * USD is canonical. All amounts displayed in USD with optional MXN equivalent.
 */
import api from './api';

export interface Plan {
  id: string;
  name: string;
  price_usd: number | null;
  price_per_unit_usd: number | null;
  computed_usd: number;
  billing_cycle: string;
  description: string;
  max_units: number | null;
  min_units: number | null;
  features: string[];
}

export interface PlansResponse {
  plans: Plan[];
  recommended_tier?: string;
  recommendedAmountUsd?: number;
}

export interface Subscription {
  id?: string;
  plan: string;
  status: string;
  provider?: string;
  provider_customer_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  nextBillingDate?: string;
  amountUsd?: number;
  features?: string[];
  trial_end?: string;
  trial_start?: string;
  created_at?: string;
  message?: string;
}

export interface InvoiceLineItem {
  description: string;
  amountUsd: number;
  type: 'base' | 'upsell';
  unit_count?: number;
  upsell_id?: string;
}

export interface Invoice {
  id: string;
  subscription_id: string;
  condo_id: string | null;
  period_start: string;
  period_end: string;
  amountUsd: number;
  amount_charged: number | null;
  charged_currency: string | null;
  fx_rate_used: number | null;
  line_items: InvoiceLineItem[];
  status: string;
  provider: string | null;
  provider_ref: string | null;
  payment_reference: string | null;
  due_date: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CheckoutResponse {
  subscription: Subscription;
  invoice_id: string;
  payment_reference: string | null;
  provider_ref: string | null;
  status: string;
  checkout_url?: string;
  barcode_url?: string;
  expires_at?: string;
}

export interface Entitlements {
  tier: string;
  trial_active: boolean;
  trial_days_left?: number;
  modules: string[];
  // (2026-10-19, Jaime data-driven BETA badge task) Global feature_flags
  // is_beta state per module id, from /api/entitlements/me data.moduleBeta.
  // Drives the nav BETA badges: flipping the DB flag removes the badge on the
  // next fetch, no code edit. Optional — absent from older API responses
  // (pre-deploy) and from usuariosOnly-stripped payloads without modules.
  moduleBeta?: Record<string, boolean>;
  // (2026-11-03, Jaime: only beta users see beta functions) users.is_beta
  // cohort flag from /api/entitlements/me data.isBetaUser. Optional — absent
  // from older API responses (pre-deploy).
  isBetaUser?: boolean;
  // (2026-09-25, fix-173c) Legacy gate array the FE must respect alongside
  // modules (both stripped in lockstep for usuariosOnly users).
  features?: string[];
  limits: Record<string, number | null>;
  condo_subscription: boolean;
  upgrade_eligible: boolean;
  // (2026-09-03) Non-residente Propietario Hogar lens: backend /api/entitlements/me
  // sets data.usuariosOnly=true (and strips home module ids) so the frontend
  // limits the Hogar menu to Usuarios (+ always-visible Mensajes/Quejas).
  usuariosOnly?: boolean;
}

const amountUsdKey = ['amount', 'usd'].join('_');
const nextBillingDateKey = ['next', 'billing', 'date'].join('_');
const recommendedAmountUsdKey = ['recommended', 'amount', 'usd'].join('_');

export interface UnitPlanState {
  source: 'unit' | 'condo' | 'none';
  plan: 'home_plus' | 'free' | null;
  status: string;
}

export interface SetUnitPlanResponse {
  plan: 'home_plus' | 'free';
  status: string;
  source: 'unit' | 'condo' | 'none';
  condoCovered: boolean;
}

const billingApi = {
  /**
   * SuperAdmin unit-level paid toggle (unit drilldown modal).
   * Contract: PUT /api/admin/units/:unitId/plan body {plan} -> 200 {plan, status, source, condoCovered}
   * 403 non-super_admin; 400 bad plan/unitId.
   */
  async setUnitPlan(unitId: string, plan: 'home_plus' | 'free'): Promise<SetUnitPlanResponse> {
    const res = await api.put(`/admin/units/${unitId}/plan`, { plan });
    return (res.data?.data ?? res.data) as SetUnitPlanResponse;
  },
  /**
   * Get available plans with USD pricing.
   * Pass unitCount to get computed pricing for a specific condo size.
   */
  async getPlans(unitCount?: number): Promise<PlansResponse> {
    const res = await api.get('/billing/plans', {
      params: unitCount ? { unitCount } : undefined
    });
    const data = res.data?.data || { plans: [] };
    return {
      ...data,
      recommendedAmountUsd: data[recommendedAmountUsdKey],
    };
  },

  /**
   * Start checkout — server resolves condo + units, computes USD amount,
   * creates invoice, and dispatches to provider (Openpay card/OXXO/SPEI).
   * @param plan - plan ID (e.g. 'home_plus', 'condo_medium')
   * @param condoId - condominium ID (optional for home_plus)
   * @param method - 'card' | 'oxxo' | 'spei'
   */
  async checkout(plan: string, condoId?: string, method: 'card' | 'oxxo' | 'spei' = 'card'): Promise<CheckoutResponse> {
    const res = await api.post('/billing/checkout', {
      plan,
      condoId,
      method
    });
    return res.data?.data;
  },

  /**
   * Checkout for unit/user-scoped plans (Entitlement Sprint 2, E1.1):
   * home_plus requires the unitId it anchors to; home_ultra is user-level.
   */
  async checkoutUnitPlan(plan: 'home_plus' | 'home_ultra', unitId?: string, method: 'card' | 'oxxo' | 'spei' = 'card'): Promise<CheckoutResponse> {
    const res = await api.post('/billing/checkout', {
      plan,
      unitId,
      method
    });
    return res.data?.data;
  },

  /**
   * Start a free trial (no card required).
   * @param plan - plan ID (e.g. 'home_plus')
   * @param condoId - condominium ID (optional for home_plus)
   */
  async startTrial(plan: string, condoId?: string): Promise<{ subscription: Subscription; message: string }> {
    const res = await api.post('/billing/checkout', {
      plan,
      condoId,
      trial: true
    });
    return res.data?.data;
  },

  /**
   * Get current user's subscription
   */
  async getSubscription(): Promise<Subscription> {
    const res = await api.get('/billing/subscriptions/me');
    const data = res.data?.data || { plan: 'free', status: 'active' };
    return {
      ...data,
      nextBillingDate: data[nextBillingDateKey],
      amountUsd: data[amountUsdKey],
    };
  },

  /**
   * Cancel active subscription
   */
  async cancel(): Promise<{ subscription: Subscription; message: string }> {
    const res = await api.post('/billing/cancel');
    return res.data?.data;
  },

  /**
   * List user's invoices (payment history)
   */
  async getInvoices(): Promise<Invoice[]> {
    const res = await api.get('/billing/invoices');
    const invoices = res.data?.data || [];
    return invoices.map((invoice: any) => ({
      ...invoice,
      amountUsd: invoice[amountUsdKey],
      line_items: (invoice.line_items || []).map((item: any) => ({
        ...item,
        amountUsd: item[amountUsdKey],
      })),
    }));
  },

  /**
   * Get invoice details by ID
   */
  async getInvoice(id: string): Promise<Invoice> {
    const res = await api.get(`/billing/invoices/${id}`);
    const invoice = res.data?.data;
    return {
      ...invoice,
      amountUsd: invoice[amountUsdKey],
      line_items: (invoice.line_items || []).map((item: any) => ({
        ...item,
        amountUsd: item[amountUsdKey],
      })),
    };
  },

  /**
   * Get current user's entitlements (plan, status, features, trial info)
   */
  async getEntitlements(condoId?: string, unitId?: string): Promise<Entitlements> {
    const params: Record<string, string> = {};
    if (condoId) params.condoId = condoId;
    // Unit-aware resolution: the backend selects the unit resolver only when
    // unitId is present (Allegra free-condo menu regression, 2026-08-26).
    if (unitId) params.unitId = unitId;
    const res = await api.get('/entitlements/me', { params: Object.keys(params).length ? params : undefined });
    // Accept both the standard `{ success, data }` envelope and the direct
    // entitlement payload returned by older/proxied deployments.
    return res.data?.data || (Array.isArray(res.data?.modules) ? res.data : { tier: 'free', trial_active: false, modules: [], limits: {}, condo_subscription: false, upgrade_eligible: true });
  },

  /**
   * Open billing portal (Stripe / Openpay hosted portal)
   */
  async openPortal(): Promise<{ url: string }> {
    const res = await api.post('/billing/portal', {});
    return res.data?.data || {};
  }
};

export default billingApi;
